import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useSearchParams } from 'next/navigation';

import { bookTickets } from '@/api/payments';
import Button from '@/components/elements/button';
import SeatSchemaForm from '@/components/event-payment/seat-schema-form';
import SubmitForm from '@/components/event-payment/submit-form';
import TicketForm from '@/components/event-payment/ticket-form';
import { FormSlide } from '@/components/event-payment/types';
import EventBookingResult from '@/components/payment-result/event-booking-result';
import { useTranslation } from '@/i18n/client';
import { selectSelectedTickets } from '@/store/event-payment/event-payment.selectotrs';
import { EventDetails } from '@/types/events';
import { WithLocale } from '@/types/platform-data';
import { PaymentProvider, PaymentProviderId, TicketsBookInput, TicketSelectOption } from '@/types/tickets';
import { storageKeys } from '@/utils/constants';

interface Props {
    ticketOptions: TicketSelectOption[];
    isVisible: boolean;
    event: EventDetails;
    onClose: () => void;
}

export default function EventPaymentForm(props: WithLocale<Props>) {
    const { t: tCo } = useTranslation(props.lang, 'common');
    const { t: tPayments } = useTranslation(props.lang, 'payments');

    const formRef = useRef<HTMLDivElement>(null);

    const params = useSearchParams();
    const initialSlide = (Number(params.get('formSlide')) || FormSlide.ticket) as FormSlide;
    const [formSlide, setFormSlide] = useState<FormSlide>(initialSlide);

    const [providersList, setProvidersList] = useState<PaymentProvider[]>([]);
    const selectedTickets = useSelector(selectSelectedTickets);

    const [bookingResult, setBookingResult] = useState<any | null>(null);
    const [paymentProvider, setPaymentProvider] = useState<PaymentProviderId | null>(null);

    useEffect(() => {
        if (formRef.current) {
            const elementTop = formRef.current.getBoundingClientRect().top + window.scrollY;
            const offset = 160;

            window.scrollTo({
                top: elementTop - offset,
                behavior: 'smooth',
            });
        }
    }, [formSlide]);

    useEffect(() => {
        const fetchProviders = async () => {
            setProvidersList([
                {
                    id: 'msu',
                    country_code: 'RS',
                    currency_code: 'RSD',
                    name: tPayments('providers.msu.name'),
                    description: tPayments('providers.msu.description'),
                },
                {
                    id: 't-bank',
                    country_code: 'RU',
                    currency_code: 'RUB',
                    name: tPayments('providers.tbank.name'),
                    description: tPayments('providers.tbank.description'),
                },
            ]);
        };
        fetchProviders();
    }, []);

    const backHandler = () => {
        if (formSlide === FormSlide.result) {
            setFormSlide(FormSlide.submit);
        }
        if (formSlide === FormSlide.submit) {
            setFormSlide(FormSlide.ticket);
        }
    };

    const handlePayment = async (formData: TicketsBookInput) => {
        const response = await bookTickets(formData);
        setPaymentProvider(formData.payment_provider);
        window.sessionStorage.setItem(storageKeys.transactionId, response.purchaseTransactionId);
        setBookingResult(response.purchaseTransactionId ? response : null);
        setFormSlide(FormSlide.result);
    };

    return (
        <>
            {/* checkout-container */}
            <div
                ref={formRef}
                className={`relative flex flex-col gap-4 overflow-hidden border border-gray-300 bg-gray-50 px-2 ${!props.isVisible && 'h-0 border-b-0'}`}
            >
                {/* navigation-container */}
                <div className="flex flex-row-reverse items-center justify-between">
                    <div onClick={props.onClose} className="flex h-9 w-9 cursor-pointer place-content-center">
                        <span className="icon w-5.5 h-5.5 rotate-180 bg-phiCaretDown"></span>
                    </div>
                    <div
                        onClick={backHandler}
                        className="h-9 cursor-pointer items-center gap-2 pl-1.5"
                        style={{ display: formSlide === FormSlide.submit ? 'flex' : 'none' }}
                    >
                        <span className="icon bg-phiArrowLeft" />
                        <p className="text-sm md:text-lg">{tCo('navigation.back')}</p>
                    </div>
                </div>

                {/* ticket-container */}
                <div
                    className="relative min-w-full flex-nowrap transition-transform duration-200"
                    style={{ display: formSlide === FormSlide.ticket ? 'flex' : 'none' }}
                >
                    {props.event.seat_schema ? (
                        <SeatSchemaForm event={props.event} ticketOptions={props.ticketOptions} lang={props.lang} />
                    ) : (
                        <TicketForm
                            lang={props.lang}
                            ticketOptions={props.ticketOptions}
                            onSlide={() => setFormSlide(FormSlide.submit)}
                        />
                    )}
                </div>
                <div
                    onClick={() => selectedTickets.length > 0 && setFormSlide(FormSlide.submit)}
                    className="md:h-20"
                    style={{ display: formSlide === FormSlide.ticket ? 'block' : 'none' }}
                >
                    <Button
                        onClick={() => setFormSlide(FormSlide.submit)}
                        title={tCo('common.buy')}
                        className={`!fixed bottom-0 right-0 z-10 !h-20 w-full cursor-pointer rounded-none transition-all duration-200 md:!absolute md:h-20 ${selectedTickets.length === 0 ? 'translate-x-full' : ''}`}
                        disabled={selectedTickets.length === 0}
                    />
                </div>

                <div
                    className="relative min-w-full flex-nowrap transition-transform duration-200"
                    style={{ display: formSlide === FormSlide.submit ? 'flex' : 'none' }}
                >
                    {providersList.length > 0 && (
                        <SubmitForm
                            isVisible={formSlide === FormSlide.submit}
                            onSubmit={handlePayment}
                            onSlideBack={() => setFormSlide(FormSlide.ticket)}
                            lang={props.lang}
                            event={props.event}
                            paymentProviders={providersList}
                        />
                    )}
                </div>

                <div
                    className="relative min-w-full flex-nowrap transition-transform duration-200"
                    style={{ display: formSlide === FormSlide.result ? 'flex' : 'none' }}
                >
                    {Boolean(paymentProvider) && (
                        <EventBookingResult
                            event={props.event}
                            paymentProvider={paymentProvider!}
                            onSlideBack={() => setFormSlide(FormSlide.submit)}
                            lang={props.lang}
                            bookingResponse={bookingResult ?? null}
                        />
                    )}
                </div>

                {/*<div className="absolute">
                    <ReCAPTCHA badge={'bottomright'} size={'invisible'} hl={props.lang} sitekey={recaptchaSiteKey} />
                </div>*/}
            </div>
        </>
    );
}
